import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, Users, Home, DollarSign, Pencil, Trash2, X, BedDouble, Bath, Square, UserPlus, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { propertyService } from '../services/propertyService';
import Sidebar from '../components/Sidebar';
import CreateTenantAccountModal from '../components/CreateTenantAccountModal';

const PropertiesPage = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [propertyForTenant, setPropertyForTenant] = useState(null);

  const [newProperty, setNewProperty] = useState({
    name: '',
    address: '',
    type: 'residential',
    status: 'vacant',
    monthlyRent: '',
    bedrooms: '',
    bathrooms: '',
    squareFootage: ''
  });

  useEffect(() => {
    if (currentUser && userData) {
      const unsubscribe = propertyService.subscribeToPropertiesByOwner(
        currentUser.uid,
        (properties) => {
          setProperties(properties || []);
          setLoading(false);
        }
      );
      return unsubscribe;
    }
  }, [currentUser, userData]);

  const handleAddProperty = async (e) => {
    e.preventDefault();
    try {
      await propertyService.createProperty({
        ...newProperty,
        ownerId: currentUser.uid,
        monthlyRent: parseFloat(newProperty.monthlyRent) || 0,
        bedrooms: parseInt(newProperty.bedrooms) || 0,
        bathrooms: parseInt(newProperty.bathrooms) || 0,
        squareFootage: parseInt(newProperty.squareFootage) || 0
      });
      setShowAddProperty(false);
      setNewProperty({
        name: '',
        address: '',
        type: 'residential',
        status: 'vacant',
        monthlyRent: '',
        bedrooms: '',
        bathrooms: '',
        squareFootage: ''
      });
      alert('Property added successfully!');
    } catch (error) {
      console.error('Error adding property:', error);
      alert('Error adding property: ' + error.message);
    }
  };

  const handleEditProperty = async (e) => {
    e.preventDefault();
    try {
      await propertyService.updateProperty(selectedProperty.id, {
        ...selectedProperty,
        monthlyRent: parseFloat(selectedProperty.monthlyRent) || 0,
        bedrooms: parseInt(selectedProperty.bedrooms) || 0,
        bathrooms: parseInt(selectedProperty.bathrooms) || 0,
        squareFootage: parseInt(selectedProperty.squareFootage) || 0
      });
      setShowEditModal(false);
      setSelectedProperty(null);
      alert('Property updated successfully!');
    } catch (error) {
      console.error('Error updating property:', error);
      alert('Error updating property: ' + error.message);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        await propertyService.deleteProperty(propertyId);
        alert('Property deleted successfully!');
      } catch (error) {
        console.error('Error deleting property:', error);
        alert('Error deleting property: ' + error.message);
      }
    }
  };



  const getPropertyStatusColor = (status) => {
    switch (status) {
      case 'occupied': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'vacant': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'maintenance': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getPropertyCardBackground = (status) => {
    switch (status) {
      case 'occupied': return 'bg-white border-slate-200 hover:border-slate-300';
      case 'vacant': return 'bg-white border-slate-200 hover:border-slate-300';
      case 'maintenance': return 'bg-white border-slate-200 hover:border-slate-300';
      default: return 'bg-white border-slate-200 hover:border-slate-300';
    }
  };

  const getPropertyTypeColor = (type) => {
    return 'bg-slate-100 text-slate-600 border-slate-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-600 mx-auto" />
          <p className="mt-4 text-sm font-medium text-slate-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10 w-full">
            <div className="flex items-center justify-between px-8 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-slate-800">Property Management</h1>
                  <p className="text-slate-500 text-sm">Manage your real estate portfolio</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm text-slate-500 pl-3 border-l border-slate-200">
                  {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                </div>
                <button
                  onClick={() => setShowAddProperty(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.75} />
                  {t('dashboard.owner.pages.properties.addProperty')}
                </button>
              </div>
            </div>
          </header>

          <div className="p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{t('dashboard.owner.pages.properties.totalProperties')}</p>
                    <p className="text-2xl font-semibold text-slate-800 mt-1 tabular-nums">{properties.length}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{t('dashboard.owner.pages.properties.inPortfolio')}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Occupied</p>
                    <p className="text-2xl font-semibold text-slate-800 mt-1 tabular-nums">{properties.filter(p => p.status === 'occupied').length}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Currently rented</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Vacant</p>
                    <p className="text-2xl font-semibold text-slate-800 mt-1 tabular-nums">{properties.filter(p => p.status === 'vacant').length}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Available for rent</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Home className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Monthly Revenue</p>
                    <p className="text-2xl font-semibold text-slate-800 mt-1 tabular-nums">${properties.reduce((total, p) => total + (p.monthlyRent || 0), 0).toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Total monthly rent</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  <div>
                    <h2 className="text-base font-semibold text-slate-800">{t('dashboard.owner.pages.properties.yourProperties')}</h2>
                    <p className="text-slate-500 text-sm">{t('dashboard.owner.pages.properties.managePortfolio')}</p>
                  </div>
                </div>
                <span className="text-sm text-slate-500 bg-slate-50 px-3 py-1.5 rounded-md font-medium">
                  {properties.length} {properties.length === 1 ? t('dashboard.owner.pages.properties.property') : t('dashboard.owner.pages.properties.properties')}
                </span>
              </div>

              {properties.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Building2 className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-semibold text-slate-800 mb-2">No properties yet</h3>
                  <p className="text-slate-500 mb-4 text-sm max-w-md mx-auto">Add your first property to start managing your portfolio.</p>
                  <button
                    onClick={() => setShowAddProperty(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.75} />
                    Add Your First Property
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {properties.map((property) => (
                    <div key={property.id} className={`${getPropertyCardBackground(property.status)} rounded-lg border p-5 transition-shadow`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                            </div>
                            <div>
                              <h3 className="text-base font-semibold text-slate-800">{property.name}</h3>
                              <p className="text-sm text-slate-500">{property.address}</p>
                            </div>
                          </div>
                                                    <div className="flex items-center space-x-2 mb-3">
                           <span className={`px-2 py-1 text-xs font-medium rounded border ${getPropertyStatusColor(property.status)}`}>
                             {property.status}
                           </span>
                           <span className={`px-2 py-1 text-xs font-medium rounded border ${getPropertyTypeColor(property.type)}`}>
                             {property.type}
                           </span>
                         </div>
                       </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedProperty(property);
                            setShowEditModal(true);
                          }}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors duration-200"
                        >
                          <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => handleDeleteProperty(property.id)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors duration-200"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      </div>
                    </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="flex items-center gap-2 mb-1">
                            <DollarSign className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.75} />
                            <p className="text-xs font-medium text-slate-500">{t('dashboard.owner.pages.properties.monthlyRent')}</p>
                          </div>
                          <p className="text-sm font-semibold text-slate-800">${property.monthlyRent?.toLocaleString() || '0'}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="flex items-center gap-2 mb-1">
                            <BedDouble className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.75} />
                            <p className="text-xs font-medium text-slate-500">Bedrooms</p>
                          </div>
                          <p className="text-sm font-semibold text-slate-800">{property.bedrooms || 'N/A'}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="flex items-center gap-2 mb-1">
                            <Bath className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.75} />
                            <p className="text-xs font-medium text-slate-500">Bathrooms</p>
                          </div>
                          <p className="text-sm font-semibold text-slate-800">{property.bathrooms || 'N/A'}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="flex items-center gap-2 mb-1">
                            <Square className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.75} />
                            <p className="text-xs font-medium text-slate-500">Square Feet</p>
                          </div>
                          <p className="text-sm font-semibold text-slate-800">{property.squareFootage?.toLocaleString() || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {property.status === 'vacant' && (
                          <button
                            onClick={() => {
                              setPropertyForTenant(property);
                              setShowCreateTenant(true);
                            }}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 transition-colors duration-200"
                          >
                            <UserPlus className="w-3.5 h-3.5" strokeWidth={1.75} />
                            Add Tenant
                          </button>
                        )}
                        <Link
                          to={`/properties/${property.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors duration-200"
                        >
                          <Eye className="w-3.5 h-3.5" strokeWidth={1.75} />
                          View Details
                        </Link>
                      </div>
                     </div>
                   ))}
                 </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Tenant Account Modal */}
      <CreateTenantAccountModal
        isOpen={showCreateTenant}
        onClose={() => {
          setShowCreateTenant(false);
          setPropertyForTenant(null);
        }}
        properties={properties}
        onTenantCreated={(result) => {
          console.log('Tenant created:', result);
          // The modal will handle showing the success message
        }}
      />

      {showAddProperty && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4 pt-20">
          <div className="relative w-full max-w-2xl shadow-xl rounded-xl bg-white border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-slate-800">{t('dashboard.owner.pages.properties.addProperty')}</h3>
              <button
                onClick={() => setShowAddProperty(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>
            <form onSubmit={handleAddProperty} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.owner.pages.properties.propertyName')}</label>
                  <input
                    type="text"
                    required
                    value={newProperty.name}
                    onChange={(e) => setNewProperty({...newProperty, name: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                    placeholder="Sunset Apartments"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.owner.pages.properties.type')}</label>
                  <select
                    value={newProperty.type}
                    onChange={(e) => setNewProperty({...newProperty, type: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.owner.pages.properties.address')}</label>
                  <input
                    type="text"
                    required
                    value={newProperty.address}
                    onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                    placeholder="123 Main Street, City, State 12345"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.owner.pages.properties.monthlyRent')} ($)</label>
                  <input
                    type="number"
                    value={newProperty.monthlyRent}
                    onChange={(e) => setNewProperty({...newProperty, monthlyRent: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                    placeholder="1500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.owner.pages.properties.status')}</label>
                  <select
                    value={newProperty.status}
                    onChange={(e) => setNewProperty({...newProperty, status: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                  >
                    <option value="vacant">Vacant</option>
                    <option value="occupied">Occupied</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Bedrooms</label>
                  <input
                    type="number"
                    value={newProperty.bedrooms}
                    onChange={(e) => setNewProperty({...newProperty, bedrooms: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                    placeholder="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Bathrooms</label>
                  <input
                    type="number"
                    value={newProperty.bathrooms}
                    onChange={(e) => setNewProperty({...newProperty, bathrooms: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                    placeholder="2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Square Footage</label>
                  <input
                    type="number"
                    value={newProperty.squareFootage}
                    onChange={(e) => setNewProperty({...newProperty, squareFootage: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                    placeholder="1500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowAddProperty(false)}
                  className="px-6 py-3 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 text-sm font-medium text-white bg-slate-700 rounded-lg hover:bg-slate-800 transition-colors duration-200"
                >
                  Add Property
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedProperty && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4 pt-20">
          <div className="relative w-full max-w-2xl shadow-xl rounded-xl bg-white border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-slate-800">{t('dashboard.owner.pages.properties.editProperty')}</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedProperty(null);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>
            <form onSubmit={handleEditProperty} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.owner.pages.properties.propertyName')}</label>
                  <input
                    type="text"
                    required
                    value={selectedProperty.name}
                    onChange={(e) => setSelectedProperty({...selectedProperty, name: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.owner.pages.properties.type')}</label>
                  <select
                    value={selectedProperty.type}
                    onChange={(e) => setSelectedProperty({...selectedProperty, type: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.owner.pages.properties.address')}</label>
                  <input
                    type="text"
                    required
                    value={selectedProperty.address}
                    onChange={(e) => setSelectedProperty({...selectedProperty, address: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.owner.pages.properties.monthlyRent')} ($)</label>
                  <input
                    type="number"
                    value={selectedProperty.monthlyRent}
                    onChange={(e) => setSelectedProperty({...selectedProperty, monthlyRent: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.owner.pages.properties.status')}</label>
                  <select
                    value={selectedProperty.status}
                    onChange={(e) => setSelectedProperty({...selectedProperty, status: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                  >
                    <option value="vacant">Vacant</option>
                    <option value="occupied">Occupied</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Bedrooms</label>
                  <input
                    type="number"
                    value={selectedProperty.bedrooms}
                    onChange={(e) => setSelectedProperty({...selectedProperty, bedrooms: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Bathrooms</label>
                  <input
                    type="number"
                    value={selectedProperty.bathrooms}
                    onChange={(e) => setSelectedProperty({...selectedProperty, bathrooms: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Square Footage</label>
                  <input
                    type="number"
                    value={selectedProperty.squareFootage}
                    onChange={(e) => setSelectedProperty({...selectedProperty, squareFootage: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedProperty(null);
                  }}
                  className="px-6 py-3 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 text-sm font-medium text-white bg-slate-700 rounded-lg hover:bg-slate-800 transition-colors duration-200"
                >
                  Update Property
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
};

export default PropertiesPage; 